import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./CommunityDetail.css";
import "./EmbeddedAttachments.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupIcon from "@mui/icons-material/Group";
import ForumIcon from "@mui/icons-material/Forum";
import SchoolIcon from "@mui/icons-material/School";
import PushPinIcon from "@mui/icons-material/PushPin";
import CampaignIcon from "@mui/icons-material/Campaign";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import BallotPickerModal from "./BallotPickerModal";
import ShortsPickerModal, { getShortTitle } from "./ShortsPickerModal";
import EmbeddedCommunityBallot from "./EmbeddedCommunityBallot";
import EmbeddedCommunityShort from "./EmbeddedCommunityShort";
import {
  getCommunityDetail,
  joinCommunity,
  leaveCommunity,
  getCommunityPosts,
  createCommunityPost,
  deleteCommunityPost,
  addCommunityComment,
  deleteCommunityComment,
  getCommunityMembers,
} from "../../../api/community";

export default function CommunityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const userDetails = useSelector((state) => state.userDetails?.userDetails);
  const currentUserId = userDetails?.userId || localStorage.getItem("userId");
  const currentUserRole = userDetails?.role || localStorage.getItem("userRole");
  const userCity = userDetails?.addresses?.[0]?.city || userDetails?.city || "";
  const userPoliticalAffiliation = userDetails?.politicalAffiliation || "";
  const userProfessions = Array.isArray(userDetails?.professions) ? userDetails.professions : [];

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("discussions"); // 'discussions' | 'members'

  // Feed State
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postTitle, setPostTitle] = useState("");
  const [postContent, setPostContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [feedFilter, setFeedFilter] = useState("all"); // 'all' | 'ballots' | 'shorts'

  // Attachment States
  const [showBallotPicker, setShowBallotPicker] = useState(false);
  const [showShortsPicker, setShowShortsPicker] = useState(false);
  const [attachedBallot, setAttachedBallot] = useState(null);
  const [attachedShort, setAttachedShort] = useState(null);

  // Comment State
  const [commentTextMap, setCommentTextMap] = useState({});
  const [commentingMap, setCommentingMap] = useState({});

  // Members State
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Joining State
  const [joining, setJoining] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCommunity = async () => {
    try {
      setLoading(true);
      const res = await getCommunityDetail(slug);
      if (res?.success) {
        setCommunity(res.data);
        if (res.data.isUnlocked) {
          fetchPosts(res.data.communityId);
          fetchMembers(res.data.communityId);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load community.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (commId) => {
    try {
      setPostsLoading(true);
      const id = commId || community?.communityId;
      if (!id) return;
      const res = await getCommunityPosts(id);
      if (res?.success) {
        setPosts(res.data || []);
      }
    } catch (err) {
      // locked or forbidden
    } finally {
      setPostsLoading(false);
    }
  };

  const fetchMembers = async (commId) => {
    try {
      setMembersLoading(true);
      const id = commId || community?.communityId;
      if (!id) return;
      const res = await getCommunityMembers(id);
      if (res?.success) {
        setMembers(res.data || []);
      }
    } catch (err) {
      // ignore
    } finally {
      setMembersLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunity();
  }, [slug]);

  // Handle incoming attachments from share action
  useEffect(() => {
    if (location.state?.attachBallot) {
      setAttachedBallot(location.state.attachBallot);
      setActiveTab("discussions");
      showToast(`📎 Ballot "${location.state.attachBallot.title || ''}" attached for discussion!`, "info");
      setTimeout(() => {
        const composerEl = document.querySelector(".post-composer-card");
        if (composerEl) {
          composerEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 350);
      window.history.replaceState({}, document.title);
    } else if (location.state?.attachShort) {
      setAttachedShort(location.state.attachShort);
      setActiveTab("discussions");
      showToast("📎 Civic short video attached for discussion!", "info");
      setTimeout(() => {
        const composerEl = document.querySelector(".post-composer-card");
        if (composerEl) {
          composerEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 350);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleJoin = async () => {
    if (!community) return;
    try {
      setJoining(true);
      const res = await joinCommunity(community.communityId);
      if (res?.success) {
        showToast("🎉 Welcome! You have joined this community.", "success");
        setCommunity((prev) => ({
          ...prev,
          isMember: true,
          memberCount: (prev?.memberCount || 0) + 1,
        }));
        fetchPosts(community.communityId);
        fetchMembers(community.communityId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to join community.", "error");
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!community) return;
    try {
      const res = await leaveCommunity(community.communityId);
      if (res?.success) {
        showToast("You have left the community.", "info");
        setCommunity((prev) => ({
          ...prev,
          isMember: false,
          memberCount: Math.max(0, (prev?.memberCount || 1) - 1),
        }));
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to leave community.", "error");
    }
  };

  const isAdmin = currentUserRole === "Admin" || currentUserRole === "SuperAdmin";
  const canParticipate = Boolean(community?.isMember || isAdmin);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postTitle.trim() || !postContent.trim()) {
      showToast("Please provide both title and content.", "warning");
      return;
    }

    try {
      setPosting(true);
      const attachedVoteId = attachedBallot?.voteId || attachedBallot?.id || null;
      const attachedVideoId = attachedShort?.videoId || attachedShort?.id || null;
      let attachmentType = "none";
      if (attachedVoteId && attachedVideoId) attachmentType = "both";
      else if (attachedVoteId) attachmentType = "ballot";
      else if (attachedVideoId) attachmentType = "short";

      const res = await createCommunityPost(community.communityId, {
        title: postTitle.trim(),
        content: postContent.trim(),
        attachedVoteId: attachedVoteId,
        attachedVideoId: attachedVideoId,
        attachmentType: attachmentType,
      });

      if (res?.success) {
        showToast("Post created successfully!", "success");
        setPostTitle("");
        setPostContent("");
        setAttachedBallot(null);
        setAttachedShort(null);
        const newPost = {
          ...res.data,
          _attachedBallotObj: attachedBallot,
          _attachedShortObj: attachedShort,
        };
        setPosts((prev) => [newPost, ...prev]);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to create post.", "error");
    } finally {
      setPosting(false);
    }
  };

  const handleAddComment = async (postId) => {
    const text = commentTextMap[postId];
    if (!text || !text.trim()) return;

    try {
      setCommentingMap((prev) => ({ ...prev, [postId]: true }));
      const res = await addCommunityComment(postId, {
        content: text.trim(),
      });
      if (res?.success) {
        setCommentTextMap((prev) => ({ ...prev, [postId]: "" }));
        setPosts((prev) =>
          prev.map((p) => {
            if (p.postId === postId) {
              return {
                ...p,
                commentsCount: (p.commentsCount || 0) + 1,
                comments: [...(p.comments || []), res.data],
              };
            }
            return p;
          })
        );
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to add comment.", "error");
    } finally {
      setCommentingMap((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const res = await deleteCommunityPost(postId);
      if (res?.success) {
        showToast("Post deleted.", "info");
        setPosts((prev) => prev.filter((p) => p.postId !== postId));
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete post.", "error");
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const res = await deleteCommunityComment(commentId);
      if (res?.success) {
        showToast("Comment deleted.", "info");
        setPosts((prev) =>
          prev.map((p) => {
            if (p.postId === postId) {
              return {
                ...p,
                commentsCount: Math.max(0, (p.commentsCount || 1) - 1),
                comments: (p.comments || []).filter((c) => c.commentId !== commentId),
              };
            }
            return p;
          })
        );
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete comment.", "error");
    }
  };

  if (loading) {
    return (
      <div className="community-detail-loading">
        <div className="community-spinner" />
        <p>Loading community space...</p>
      </div>
    );
  }

  if (!community) {
    return (
      <div className="community-detail-error">
        <h2>Community Not Found</h2>
        <button onClick={() => navigate("/communities")} className="btn-back">
          <ArrowBackIcon /> Back to Communities
        </button>
      </div>
    );
  }

  const isCityCommunity = community.communityType === "City";
  const isPoliticalCommunity = community.communityType === "Political";
  const isProfessionCommunity = community.communityType === "Profession";

  return (
    <div className="CommunityDetailContainer">
      {/* ─── Top Back Navigation Bar ─── */}
      <div className="community-detail-nav-bar">
        <button onClick={() => navigate("/communities")} className="community-back-btn">
          <ArrowBackIcon style={{ fontSize: 18 }} />
          <span>Back to Communities</span>
        </button>
      </div>

      {/* ─── Community Hero Header Card ─── */}
      <div className="community-hero-header-card">
        {/* Banner with Background Image */}
        <div
          className="community-hero-banner"
          style={{
            backgroundImage: community.coverImageUrl ? `url(${community.coverImageUrl})` : "none",
          }}
        >
          <div className="community-hero-banner-overlay" />
          <div className="community-hero-top-badges">
            {isCityCommunity ? (
              <span className="community-type-pill city-type-pill">
                <LocationCityIcon style={{ fontSize: 13 }} /> City Space
              </span>
            ) : isPoliticalCommunity ? (
              <span className="community-type-pill political-type-pill">
                <HowToVoteRoundedIcon style={{ fontSize: 13 }} /> Political Space
              </span>
            ) : isProfessionCommunity ? (
              <span className="community-type-pill profession-type-pill">
                <WorkOutlineRoundedIcon style={{ fontSize: 13 }} /> Professional Circle
              </span>
            ) : (
              <span className="community-type-pill">
                {community.isGated ? "Merit Space" : "Open Forum"}
              </span>
            )}
          </div>
        </div>

        {/* Hero Identity Content */}
        <div className="community-hero-content-wrapper">
          <div className="community-hero-avatar-box">
            {community.avatarUrl ? (
              <img src={community.avatarUrl} alt={community.name} className="community-hero-avatar" />
            ) : (
              <div className={`community-hero-avatar-placeholder ${isCityCommunity ? "city-avatar" : ""} ${isPoliticalCommunity ? "political-avatar" : ""} ${isProfessionCommunity ? "profession-avatar" : ""}`}>
                {isCityCommunity ? (
                  <LocationCityIcon style={{ fontSize: 32 }} />
                ) : isPoliticalCommunity ? (
                  <HowToVoteRoundedIcon style={{ fontSize: 32 }} />
                ) : isProfessionCommunity ? (
                  <WorkOutlineRoundedIcon style={{ fontSize: 32 }} />
                ) : (
                  community.name.charAt(0).toUpperCase()
                )}
              </div>
            )}
          </div>

          <div className="community-hero-details">
            <div className="community-hero-title-row">
              <div className="community-hero-main-info">
                <div className="community-title-badges-wrap">
                  <h1 className="community-hero-title">{community.name}</h1>
                  {isCityCommunity ? (
                    <span className="badge-city-pill">
                      <LocationOnIcon style={{ fontSize: 13 }} /> Local Residency: {community.city}{community.state ? `, ${community.state}` : ""}
                    </span>
                  ) : isPoliticalCommunity ? (
                    <span className="badge-political-pill">
                      <HowToVoteRoundedIcon style={{ fontSize: 13 }} /> Political Affiliation: {community.politicalAffiliation || community.name}
                    </span>
                  ) : isProfessionCommunity ? (
                    <span className="badge-profession-pill">
                      <WorkOutlineRoundedIcon style={{ fontSize: 13 }} /> Professional Circle: {community.profession || community.name}
                    </span>
                  ) : community.isGated ? (
                    <span className="badge-gated-pill">
                      <LockIcon style={{ fontSize: 13 }} /> Gated by Merit: {community.requiredCertificationName}
                    </span>
                  ) : (
                    <span className="badge-open-pill">
                      <LockOpenIcon style={{ fontSize: 13 }} /> Open Community
                    </span>
                  )}
                </div>

                <p className="community-hero-description">{community.description || "No description provided."}</p>

                <div className="community-hero-meta-row">
                  <span className="meta-chip">
                    <GroupIcon style={{ fontSize: 15 }} />
                    <strong>{community.memberCount}</strong> {community.memberCount === 1 ? "Member" : "Members"}
                  </span>
                  {community.isMember && (
                    <span className="meta-chip verified-member-chip">
                      <CheckCircleIcon style={{ fontSize: 15 }} />
                      Verified Member
                    </span>
                  )}
                </div>
              </div>

              {/* Join CTA if unlocked but not joined */}
              {community.isUnlocked && !community.isMember && (
                <div className="community-hero-cta">
                  <button className="btn-hero-join" onClick={handleJoin} disabled={joining}>
                    {joining ? "Joining..." : "Join Community"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── LOCKED STATE: If user is not qualified ─── */}
      {!community.isUnlocked ? (
        <div className="community-locked-container">
          {isCityCommunity ? (
            <div className="locked-shield-card city-locked-card">
              <div className="locked-icon-wrapper city-locked-icon">
                <LocationOnIcon style={{ fontSize: 44, color: "#14b8a6" }} />
              </div>
              <h2 className="locked-title">Exclusive Local City Space</h2>
              <p className="locked-desc">
                This community space is reserved exclusively for verified residents of <strong>{community.city}{community.state ? `, ${community.state}` : ""}</strong>.
                To join discussions and collaborate on local initiatives, you must have your registered address in this city.
              </p>

              {/* Requirement Details */}
              <div className="locked-requirements-card">
                <div className="req-row">
                  <span className="req-label">Required Location</span>
                  <span className="req-value" style={{ color: "#14b8a6", fontWeight: 600 }}>
                    📍 {community.city}{community.state ? `, ${community.state}` : ""}{community.country ? `, ${community.country}` : ""}
                  </span>
                </div>
                <div className="req-row">
                  <span className="req-label">Your Current Registered City</span>
                  <span className="req-value" style={{ color: userCity ? "#fff" : "#94a3b8" }}>
                    {userCity ? `📍 ${userCity}` : "No address registered"}
                  </span>
                </div>
              </div>

              <button
                className="btn-unlock-city"
                onClick={() => navigate("/profile-details/edit-address")}
              >
                <LocationOnIcon style={{ fontSize: 18 }} /> Update Address in Profile to Unlock
              </button>
            </div>
          ) : isPoliticalCommunity ? (
            <div className="locked-shield-card political-locked-card">
              <div className="locked-icon-wrapper political-locked-icon">
                <HowToVoteRoundedIcon style={{ fontSize: 44, color: "#48D1CC" }} />
              </div>
              <h2 className="locked-title">Exclusive Political Party Circle</h2>
              <p className="locked-desc">
                This space is reserved exclusively for verified supporters of <strong>{community.politicalAffiliation || community.name}</strong>.
                To join internal discussions, participate in civic ballot formulation, and collaborate with members, update your political affiliation in profile details.
              </p>

              {/* Requirement Details */}
              <div className="locked-requirements-card">
                <div className="req-row">
                  <span className="req-label">Required Political Affiliation</span>
                  <span className="req-value" style={{ color: "#48D1CC", fontWeight: 600 }}>
                    🗳️ {community.politicalAffiliation || community.name}
                  </span>
                </div>
                <div className="req-row">
                  <span className="req-label">Your Current Affiliation</span>
                  <span className="req-value" style={{ color: userPoliticalAffiliation ? "#fff" : "#94a3b8" }}>
                    {userPoliticalAffiliation ? `🗳️ ${userPoliticalAffiliation}` : "None selected"}
                  </span>
                </div>
              </div>

              <button
                className="btn-unlock-political"
                onClick={() => navigate("/profile-details/edit-political-affiliation")}
              >
                <HowToVoteRoundedIcon style={{ fontSize: 18 }} /> Update Political Affiliation in Profile to Unlock
              </button>
            </div>
          ) : isProfessionCommunity ? (
            <div className="locked-shield-card profession-locked-card">
              <div className="locked-icon-wrapper profession-locked-icon">
                <WorkOutlineRoundedIcon style={{ fontSize: 44, color: "#48D1CC" }} />
              </div>
              <h2 className="locked-title">Exclusive Professional Circle</h2>
              <p className="locked-desc">
                This space is reserved exclusively for verified <strong>{community.profession || community.name}s</strong>.
                To join internal discussions, formulate industry ballots, and collaborate with fellow professionals, add this title to your profile details.
              </p>

              {/* Requirement Details */}
              <div className="locked-requirements-card">
                <div className="req-row">
                  <span className="req-label">Required Profession / Title</span>
                  <span className="req-value" style={{ color: "#48D1CC", fontWeight: 600 }}>
                    💼 {community.profession || community.name}
                  </span>
                </div>
                <div className="req-row">
                  <span className="req-label">Your Current Titles</span>
                  <span className="req-value" style={{ color: userProfessions.length > 0 ? "#fff" : "#94a3b8" }}>
                    {userProfessions.length > 0 ? userProfessions.join(", ") : "None selected"}
                  </span>
                </div>
              </div>

              <button
                className="btn-unlock-profession"
                onClick={() => navigate("/profile-details/edit-professions")}
              >
                <WorkOutlineRoundedIcon style={{ fontSize: 18 }} /> Add Title in Profile to Unlock
              </button>
            </div>
          ) : (
            <div className="locked-shield-card">
              <div className="locked-icon-wrapper">
                <LockIcon style={{ fontSize: 44, color: "#d4af37" }} />
              </div>
              <h2 className="locked-title">Exclusive Merit-Gated Space</h2>
              <p className="locked-desc">
                This space is gated by merit and verified learning. To join and participate in exclusive decision-making, discussions, and initiatives, you must hold the qualifying credential:
              </p>

              {/* Requirement Details */}
              <div className="locked-requirements-card">
                <div className="req-row">
                  <span className="req-label">Required Certification</span>
                  <span className="req-value">{community.requiredCertificationName}</span>
                </div>
                {community.minRequiredBadgeLevel && (
                  <div className="req-row">
                    <span className="req-label">Required Badge Tier</span>
                    <span className="req-value" style={{ color: "#48D1CC", textTransform: "uppercase" }}>
                      {community.minRequiredBadgeLevel}
                    </span>
                  </div>
                )}
                {community.minRequiredScore && (
                  <div className="req-row">
                    <span className="req-label">Minimum Exam Score</span>
                    <span className="req-value">{community.minRequiredScore}%</span>
                  </div>
                )}
              </div>

              <button
                className="btn-unlock-cert"
                onClick={() =>
                  navigate(
                    community.requiredCertificationId
                      ? `/certifications/${community.requiredCertificationId}`
                      : "/certifications"
                  )
                }
              >
                <SchoolIcon style={{ fontSize: 18 }} /> Earn Certification & Unlock Community
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ─── UNLOCKED STATE: Community Feed & Discussions ─── */
        <div className="community-content-area">
          {/* Tabs */}
          <div className="community-tabs-header">
            <button
              className={`community-tab-nav ${activeTab === "discussions" ? "active" : ""}`}
              onClick={() => setActiveTab("discussions")}
            >
              <ForumIcon style={{ fontSize: 16 }} /> Discussions ({posts.length})
            </button>
            <button
              className={`community-tab-nav ${activeTab === "members" ? "active" : ""}`}
              onClick={() => setActiveTab("members")}
            >
              <GroupIcon style={{ fontSize: 16 }} /> Verified Members ({members.length})
            </button>
          </div>

          {activeTab === "discussions" ? (
            <div className="discussions-layout">
              {/* Post Composer */}
              <div className={`post-composer-card ${!canParticipate ? "composer-disabled" : ""}`}>
                {!canParticipate && (
                  <div className="composer-disabled-banner">
                    <div className="composer-disabled-text">
                      <LockIcon style={{ fontSize: 16, color: "#48D1CC" }} />
                      <span>Join this community to start discussions and post replies.</span>
                    </div>
                    <button
                      type="button"
                      className="btn-inline-join"
                      onClick={handleJoin}
                      disabled={joining}
                    >
                      {joining ? "Joining..." : "Join Community"}
                    </button>
                  </div>
                )}
                <h3 className="composer-title">Start a Discussion</h3>
                <form onSubmit={handleCreatePost}>
                  <input
                    type="text"
                    placeholder={canParticipate ? "Discussion Title..." : "Join community to start a discussion..."}
                    value={postTitle}
                    onChange={(e) => setPostTitle(e.target.value)}
                    className="composer-input-title"
                    disabled={!canParticipate || posting}
                  />
                  <textarea
                    rows="3"
                    placeholder={
                      canParticipate
                        ? "Share insights, propose ideas, or ask your verified peers..."
                        : "You must join this community to share insights and participate in discussions..."
                    }
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    className="composer-textarea"
                    disabled={!canParticipate || posting}
                  />

                  {/* Attached Previews inside Composer */}
                  {(attachedBallot || attachedShort) && (
                    <div className="composer-attachments-tray">
                      {attachedBallot && (
                        <div className="composer-attachment-preview ballot-preview">
                          <div className="composer-preview-icon">
                            <HowToVoteRoundedIcon style={{ fontSize: 18, color: "#48D1CC" }} />
                          </div>
                          <div className="composer-preview-info">
                            <span className="composer-preview-label">Attached Ballot</span>
                            <h5 className="composer-preview-title">{attachedBallot.title}</h5>
                            <span className="composer-preview-sub">
                              {(attachedBallot.votingOption?.length || attachedBallot.options?.length || 0)} Options
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-composer-remove-attachment"
                            onClick={() => setAttachedBallot(null)}
                            title="Remove attached ballot"
                          >
                            <CloseIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      )}

                      {attachedShort && (
                        <div className="composer-attachment-preview shorts-preview">
                          <div className="composer-preview-media">
                            {attachedShort.thumbnailUrl ? (
                              <img
                                src={attachedShort.thumbnailUrl}
                                alt={attachedShort.title || "Short thumbnail"}
                                className="composer-preview-thumb"
                              />
                            ) : (
                              <div className="composer-preview-thumb-placeholder">
                                <VideoLibraryRoundedIcon style={{ fontSize: 18, color: "#2dd4bf" }} />
                              </div>
                            )}
                          </div>
                          <div className="composer-preview-info">
                            <span className="composer-preview-label">Attached Short</span>
                            <h5 className="composer-preview-title">{getShortTitle(attachedShort)}</h5>
                            <span className="composer-preview-sub">
                              {attachedShort.duration ? `${Math.floor(attachedShort.duration / 60)}:${(attachedShort.duration % 60).toString().padStart(2, "0")}` : "Video"}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-composer-remove-attachment"
                            onClick={() => setAttachedShort(null)}
                            title="Remove attached short"
                          >
                            <CloseIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="composer-actions">
                    {/* Attachment Buttons Toolbar */}
                    <div className="composer-attachment-buttons">
                      <button
                        type="button"
                        className={`btn-composer-attach ${attachedBallot ? "attached" : ""}`}
                        onClick={() => setShowBallotPicker(true)}
                        disabled={!canParticipate || posting}
                        title="Attach an active ballot to this discussion"
                      >
                        <HowToVoteRoundedIcon style={{ fontSize: 16 }} />
                        <span>{attachedBallot ? "Ballot Attached" : "Attach Ballot"}</span>
                      </button>

                      <button
                        type="button"
                        className={`btn-composer-attach ${attachedShort ? "attached" : ""}`}
                        onClick={() => setShowShortsPicker(true)}
                        disabled={!canParticipate || posting}
                        title="Attach a shorts video to this discussion"
                      >
                        <VideoLibraryRoundedIcon style={{ fontSize: 16 }} />
                        <span>{attachedShort ? "Short Attached" : "Attach Short"}</span>
                      </button>
                    </div>

                    <button type="submit" className="btn-post-submit" disabled={!canParticipate || posting}>
                      {posting ? "Publishing..." : <>Post Discussion <SendIcon style={{ fontSize: 14 }} /></>}
                    </button>
                  </div>
                </form>
              </div>

              {/* Feed List */}
              {postsLoading ? (
                <div className="feed-loading">
                  <div className="community-spinner" />
                </div>
              ) : posts.length === 0 ? (
                <div className="feed-empty">
                  <ForumIcon style={{ fontSize: 40, opacity: 0.25 }} />
                  <h4>No discussions yet</h4>
                  <p>Start the first conversation with your certified peers above!</p>
                </div>
              ) : (
                <div className="feed-posts-list">
                  {/* Filter Toolbar */}
                  <div className="feed-filter-bar">
                    <button
                      className={`feed-filter-btn ${feedFilter === "all" ? "active" : ""}`}
                      onClick={() => setFeedFilter("all")}
                    >
                      All Discussions ({posts.length})
                    </button>
                    <button
                      className={`feed-filter-btn ${feedFilter === "ballots" ? "active" : ""}`}
                      onClick={() => setFeedFilter("ballots")}
                    >
                      <HowToVoteRoundedIcon style={{ fontSize: 14 }} /> With Ballots ({posts.filter((p) => p.attachedVoteId).length})
                    </button>
                    <button
                      className={`feed-filter-btn ${feedFilter === "shorts" ? "active" : ""}`}
                      onClick={() => setFeedFilter("shorts")}
                    >
                      <VideoLibraryRoundedIcon style={{ fontSize: 14 }} /> With Shorts ({posts.filter((p) => p.attachedVideoId).length})
                    </button>
                  </div>

                  {posts
                    .filter((post) => {
                      if (feedFilter === "ballots") return Boolean(post.attachedVoteId);
                      if (feedFilter === "shorts") return Boolean(post.attachedVideoId);
                      return true;
                    })
                    .map((post) => (
                    <div
                      key={post.postId}
                      className={`post-card ${post.isAnnouncement ? "announcement" : ""} ${
                        post.isPinned ? "pinned" : ""
                      }`}
                    >
                      {/* Post Top Badges */}
                      {(post.isAnnouncement || post.isPinned || post.attachedVoteId || post.attachedVideoId) && (
                        <div className="post-badges-row">
                          {post.isAnnouncement && (
                            <span className="badge-announcement">
                              <CampaignIcon style={{ fontSize: 13 }} /> Official Announcement
                            </span>
                          )}
                          {post.isPinned && (
                            <span className="badge-pinned">
                              <PushPinIcon style={{ fontSize: 13 }} /> Pinned
                            </span>
                          )}
                          {post.attachedVoteId && (
                            <span className="post-attached-badge post-ballot-badge">
                              <HowToVoteRoundedIcon style={{ fontSize: 12 }} /> Attached Ballot
                            </span>
                          )}
                          {post.attachedVideoId && (
                            <span className="post-attached-badge post-short-badge">
                              <VideoLibraryRoundedIcon style={{ fontSize: 12 }} /> Attached Short
                            </span>
                          )}
                        </div>
                      )}

                      {/* Author Header */}
                      <div className="post-author-row">
                        {post.authorProfileImage ? (
                          <img src={post.authorProfileImage} alt={post.authorName} className="post-author-avatar" />
                        ) : (
                          <div className="post-author-avatar-placeholder">
                            {post.authorName?.charAt(0).toUpperCase() || "U"}
                          </div>
                        )}
                        <div className="post-author-meta">
                          <div className="post-author-name">
                            {post.authorName}
                            {post.authorRole?.includes("Admin") && (
                              <span className="badge-admin-tag">Staff</span>
                            )}
                            {post.authorBadgeType && (
                              <span className="badge-tier-tag">
                                {post.authorBadgeType.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <span className="post-date">
                            {new Date(post.createdOn).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>

                        {(() => {
                          const isPostAuthor = Boolean(
                            currentUserId &&
                            post.userId &&
                            String(post.userId).toLowerCase() === String(currentUserId).toLowerCase()
                          );
                          const canDeletePost = isPostAuthor || currentUserRole === "Admin" || currentUserRole === "SuperAdmin";

                          return canDeletePost ? (
                            <button
                              className="btn-delete-post"
                              onClick={() => handleDeletePost(post.postId)}
                              title="Delete discussion"
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </button>
                          ) : null;
                        })()}
                      </div>

                      {/* Content */}
                      <h4 className="post-title">{post.title}</h4>
                      <p className="post-body">{post.content}</p>

                      {/* Embedded Attached Ballot */}
                      {post.attachedVoteId && (
                        <EmbeddedCommunityBallot
                          voteId={post.attachedVoteId}
                          initialData={post._attachedBallotObj}
                        />
                      )}

                      {/* Embedded Attached Shorts Video */}
                      {post.attachedVideoId && (
                        <EmbeddedCommunityShort
                          videoId={post.attachedVideoId}
                          initialData={post._attachedShortObj}
                        />
                      )}

                      {/* Comments Section */}
                      <div className="post-comments-box">
                        <div className="comments-count-label">
                          <ChatBubbleOutlineIcon style={{ fontSize: 14 }} /> {post.comments?.length || 0} Replies
                        </div>

                        {post.comments?.map((comment) => (
                          <div key={comment.commentId} className="comment-item">
                            <div className="comment-author-name">
                              {comment.authorName}
                              {comment.authorRole?.includes("Admin") && (
                                <span className="badge-admin-tag">Staff</span>
                              )}
                              <span className="comment-time">
                                {new Date(comment.createdOn).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              {(() => {
                                const isCommentAuthor = Boolean(
                                  currentUserId &&
                                  comment.userId &&
                                  String(comment.userId).toLowerCase() === String(currentUserId).toLowerCase()
                                );
                                const canDeleteComment = isCommentAuthor || currentUserRole === "Admin" || currentUserRole === "SuperAdmin";

                                return canDeleteComment ? (
                                  <button
                                    className="btn-delete-comment"
                                    onClick={() => handleDeleteComment(comment.commentId)}
                                    title="Delete reply"
                                  >
                                    <DeleteOutlineIcon style={{ fontSize: 13 }} />
                                  </button>
                                ) : null;
                              })()}
                            </div>
                            <p className="comment-text">{comment.content}</p>
                          </div>
                        ))}

                        {/* Reply Form */}
                        <div className="comment-reply-form">
                          <input
                            type="text"
                            placeholder={canParticipate ? "Write a reply..." : "Join community to reply..."}
                            value={commentTextMap[post.postId] || ""}
                            onChange={(e) =>
                              setCommentTextMap((prev) => ({ ...prev, [post.postId]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !commentingMap[post.postId] && canParticipate) {
                                handleAddComment(post.postId);
                              }
                            }}
                            className="reply-input"
                            disabled={!canParticipate || commentingMap[post.postId]}
                          />
                          <button
                            className="btn-reply-send"
                            onClick={() => handleAddComment(post.postId)}
                            disabled={!canParticipate || commentingMap[post.postId] || !commentTextMap[post.postId]?.trim()}
                          >
                            {commentingMap[post.postId] ? (
                              <CircularProgress size={14} style={{ color: "#091714" }} />
                            ) : (
                              "Reply"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* TAB 2: Certified Members Roster */
            <div className="members-grid">
              {membersLoading ? (
                <div className="feed-loading">
                  <div className="community-spinner" />
                </div>
              ) : members.length === 0 ? (
                <div className="feed-empty">
                  <GroupIcon style={{ fontSize: 40, opacity: 0.25 }} />
                  <h4>No members found</h4>
                </div>
              ) : (
                members.map((member) => {
                  return (
                    <div key={member.communityMemberId} className="member-card">
                      {member.profileImage ? (
                        <img src={member.profileImage} alt={member.firstName} className="member-avatar" />
                      ) : (
                        <div className="member-avatar-placeholder">
                          {(member.firstName || "U").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="member-info">
                        <h4 className="member-name">
                          {member.firstName} {member.lastName}
                        </h4>
                        <span className="member-username">@{member.userName || "user"}</span>

                        <div className="member-badges-row">
                          {member.badgeType ? (
                            <span className="member-badge-tier">
                              <VerifiedIcon style={{ fontSize: 11, marginRight: 2 }} />
                              {member.badgeType?.toUpperCase() || "VERIFIED"} ({member.score}%)
                            </span>
                          ) : member.role === "Admin" ? (
                            <span className="badge-admin-tag">Staff Admin</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals for Ballot & Shorts Picker */}
      <BallotPickerModal
        open={showBallotPicker}
        onClose={() => setShowBallotPicker(false)}
        onSelectBallot={(b) => {
          setAttachedBallot(b);
          showToast(`Attached ballot: "${b.title}"`, "success");
        }}
        selectedBallotId={attachedBallot?.voteId || attachedBallot?.id}
      />

      <ShortsPickerModal
        open={showShortsPicker}
        onClose={() => setShowShortsPicker(false)}
        onSelectShort={(s) => {
          setAttachedShort(s);
          showToast(`Attached short: "${getShortTitle(s)}"`, "success");
        }}
        selectedShortId={attachedShort?.videoId || attachedShort?.id}
      />

      {/* Toast Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
